import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdLocalOffer, MdAdd, MdEdit, MdDelete, MdSearch } from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import ConfirmModal from '@/components/confirm-modal'
import {
    getPromotionsByCompany,
    deletePromotion,
    Promotion,
} from '@/lib/api/commerce/promos'

const typeLabels: Record<string, string> = {
    ALL_STORE: 'Toda a Loja',
    CATEGORY: 'Categoria',
    SUBCATEGORY: 'Subcategoria',
    MINIMUM_PURCHASE: 'Compra Mínima',
    SPECIFIC_PRODUCTS: 'Produtos Específicos',
}

const typeBadgeColors: Record<string, string> = {
    ALL_STORE: 'badge-neutral',
    CATEGORY: 'badge-secondary',
    SUBCATEGORY: 'badge-secondary',
    MINIMUM_PURCHASE: 'badge-accent',
    SPECIFIC_PRODUCTS: 'badge-info',
}

function getPromotionStatus(promo: Promotion): {
    label: string
    className: string
} {
    if (!promo.active) {
        return { label: 'Inativa', className: 'badge-error' }
    }

    const now = new Date()
    const end = new Date(promo.endDate)

    if (end < now) {
        return { label: 'Expirada', className: 'badge-warning' }
    }

    return { label: 'Ativa', className: 'badge-success' }
}

function formatDiscountValue(promo: Promotion): string {
    if (promo.discountType === 'PERCENTAGE') {
        return `${promo.discountValue}%`
    }
    return `R$ ${(promo.discountValue / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatDate(dateStr: string): string {
    const date = new Date(dateStr)
    return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    })
}

export default function PromosListPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'commerce',
        'promos'
    )

    const [promotions, setPromotions] = useState<Promotion[]>([])
    const [filteredPromotions, setFilteredPromotions] = useState<Promotion[]>(
        []
    )
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [promoToDelete, setPromoToDelete] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (user?.companyId) {
            loadPromotions()
        }
    }, [user?.companyId])

    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredPromotions(promotions)
            return
        }

        const term = searchTerm.toLowerCase()
        const filtered = promotions.filter(
            promo =>
                promo.name.toLowerCase().includes(term) ||
                promo.description?.toLowerCase().includes(term) ||
                typeLabels[promo.type]?.toLowerCase().includes(term) ||
                promo.category?.name.toLowerCase().includes(term) ||
                promo.subcategory?.name.toLowerCase().includes(term)
        )
        setFilteredPromotions(filtered)
    }, [searchTerm, promotions])

    const loadPromotions = async () => {
        if (!user?.companyId) return

        setLoading(true)
        try {
            const response = await getPromotionsByCompany(user.companyId)
            if (response.success) {
                setPromotions(response.result)
                setFilteredPromotions(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar promoções')
            }
        } catch (error) {
            toast.error('Erro ao carregar promoções')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = (promoId: number) => {
        setPromoToDelete(promoId)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!promoToDelete) return

        setDeleting(true)
        try {
            const response = await deletePromotion(promoToDelete)
            if (response.success) {
                toast.success('Promoção excluída com sucesso')
                loadPromotions()
                setShowDeleteModal(false)
                setPromoToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir promoção')
            }
        } catch (error) {
            toast.error('Erro ao excluir promoção')
        } finally {
            setDeleting(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando promoções...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdLocalOffer className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Promoções
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {promotions.length}{' '}
                            {promotions.length === 1
                                ? 'promoção cadastrada'
                                : 'promoções cadastradas'}
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={() => navigate('/commerce/promos/new')}
                    >
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">Adicionar</span>
                    </button>
                )}
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome, descrição, tipo, categoria..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {filteredPromotions.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdLocalOffer className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhuma promoção encontrada'
                                : 'Nenhuma promoção cadastrada'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Comece adicionando uma promoção'}
                        </p>
                        {!searchTerm && canCreate && (
                            <button
                                onClick={() => navigate('/commerce/promos/new')}
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar primeira promoção
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {filteredPromotions.map(promo => {
                        const status = getPromotionStatus(promo)

                        return (
                            <div
                                key={promo.id}
                                className="group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-lg transition-all duration-300"
                            >
                                <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4">
                                    <div className="relative w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 bg-gradient-to-br from-base-200 to-base-300/50 rounded-xl flex items-center justify-center">
                                        <MdLocalOffer className="text-2xl sm:text-3xl text-base-content/20" />
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2 mb-1">
                                            <h3 className="text-sm sm:text-base font-semibold sm:font-bold text-base-content line-clamp-1 break-words">
                                                {promo.name}
                                            </h3>
                                            <span
                                                className={`badge badge-sm ${status.className} whitespace-nowrap`}
                                            >
                                                {status.label}
                                            </span>
                                        </div>

                                        <div className="flex flex-wrap items-center gap-1.5 mb-2">
                                            <span
                                                className={`badge badge-xs sm:badge-sm ${typeBadgeColors[promo.type]} badge-outline`}
                                            >
                                                {typeLabels[promo.type]}
                                            </span>
                                            {promo.category && (
                                                <span className="badge badge-xs sm:badge-sm badge-ghost">
                                                    {promo.category.name}
                                                </span>
                                            )}
                                            {promo.subcategory && (
                                                <span className="badge badge-xs sm:badge-sm badge-ghost">
                                                    {promo.subcategory.name}
                                                </span>
                                            )}
                                        </div>

                                        {promo.description && (
                                            <p className="text-xs sm:text-sm text-base-content/70 mb-2 line-clamp-2 break-words">
                                                {promo.description}
                                            </p>
                                        )}

                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 pt-2 sm:pt-3 border-t border-base-300/50">
                                            <div className="flex items-center gap-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="p-1.5 sm:p-2 bg-secondary/10 rounded-lg">
                                                        <MdLocalOffer className="text-base sm:text-lg text-secondary" />
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-base-content/60">
                                                            Desconto
                                                        </p>
                                                        <p className="text-sm sm:text-base font-bold text-secondary">
                                                            {formatDiscountValue(
                                                                promo
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div>
                                                    <p className="text-xs text-base-content/60">
                                                        Período
                                                    </p>
                                                    <p className="text-xs sm:text-sm font-medium text-base-content">
                                                        {formatDate(
                                                            promo.startDate
                                                        )}{' '}
                                                        -{' '}
                                                        {formatDate(
                                                            promo.endDate
                                                        )}
                                                    </p>
                                                </div>
                                            </div>

                                            {(canUpdate || canDelete) && (
                                                <div className="flex items-center gap-2">
                                                    {canUpdate && (
                                                        <button
                                                            onClick={() =>
                                                                navigate(
                                                                    `/commerce/promos/edit/${promo.id}`
                                                                )
                                                            }
                                                            className="btn btn-xs sm:btn-sm btn-ghost"
                                                            title="Editar"
                                                        >
                                                            <MdEdit className="text-base sm:text-lg" />
                                                        </button>
                                                    )}
                                                    {canDelete && (
                                                        <button
                                                            onClick={() =>
                                                                handleDelete(
                                                                    promo.id
                                                                )
                                                            }
                                                            className="btn btn-xs sm:btn-sm btn-ghost text-error hover:bg-error/10"
                                                            title="Excluir"
                                                        >
                                                            <MdDelete className="text-base sm:text-lg" />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setPromoToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir promoção"
                message="Tem certeza de que deseja excluir esta promoção? Esta ação não pode ser desfeita."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
